import React, { useEffect, useState, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import axios from "axios";
import { FaEdit, FaTrash, FaFilter, FaBoxOpen, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { MdOutlineAddBox } from "react-icons/md";
import { baseurl, imgurl } from "../../baseurl";
import { Link } from "react-router-dom";

function AllProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [filters, setFilters] = useState({
    search: "",
    category: "All",
    status: "All",
    stock: "All",
    priceMin: "",
    priceMax: "",
    dateFrom: null,
    dateTo: null,
  });

  const vendorId = localStorage.getItem("vendorId");

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getVendorProducts/${vendorId}`);
      if (res.data.success) {
        setProducts(res.data.products || []);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filter logic
  const filteredProducts = useMemo(() => {
    const result = products.filter((p) => {
      const matchesSearch =
        p.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        p.sku?.toLowerCase().includes(filters.search.toLowerCase());

      const matchesCategory =
        filters.category === "All" || p.category_name === filters.category;

      const matchesStock =
        filters.stock === "All" ||
        (filters.stock === "InStock" && p.current_stock > 0) ||
        (filters.stock === "OutOfStock" && p.current_stock === 0);

      const matchesPrice =
        (filters.priceMin === "" || p.price >= Number(filters.priceMin)) &&
        (filters.priceMax === "" || p.price <= Number(filters.priceMax));

      const matchesDate =
        (!filters.dateFrom ||
          new Date(p.created_at) >= new Date(filters.dateFrom)) &&
        (!filters.dateTo || new Date(p.created_at) <= new Date(filters.dateTo));

      return (
        matchesSearch &&
        matchesCategory &&
        matchesStock &&
        matchesPrice &&
        matchesDate
      );
    });

    setCurrentPage(1);
    return result;
  }, [products, filters]);

  // Pagination Calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Logic to get the range of page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisibleButtons = 7; // Adjust this to show more/less buttons

    if (totalPages <= maxVisibleButtons) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      // Always show first page
      pageNumbers.push(1);

      if (currentPage > 3) {
        pageNumbers.push("...");
      }

      // Show pages around current page
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) end = 4;
      if (currentPage >= totalPages - 2) start = totalPages - 3;

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      if (currentPage < totalPages - 2) {
        pageNumbers.push("...");
      }

      // Always show last page
      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleDateChange = (name, date) => {
    setFilters({ ...filters, [name]: date[0] });
  };

  const categories = ["All", ...new Set(products.map((p) => p.category_name))];

  const SkeletonRow = () => (
    <tr>
      {Array(10).fill(0).map((_, i) => (
        <td key={i}><div className="skeleton-line"></div></td>
      ))}
    </tr>
  );

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="main-content">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="breadcrumb-title d-flex align-items-center gap-2">
              <FaBoxOpen /> All Products
            </h4>
            <div className="d-flex gap-2">
              <Link to="/product-create" className="btn btn-primary d-flex align-items-center gap-1">
                <MdOutlineAddBox /> Add Product
              </Link>
              <button className="btn btn-primary d-flex align-items-center gap-1" onClick={() => setShowFilter(true)}>
                <FaFilter /> Filters
              </button>
            </div>
          </div>

          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body table-responsive">
                <table className="table table-hover table-striped align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Image</th>
                      <th>Name</th>
                      <th>SKU</th>
                      <th>Category</th>
                      <th>Subcategory</th>
                      <th>Price</th>
                      <th>Stock</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array(5).fill(0).map((_, idx) => <SkeletonRow key={idx} />)
                    ) : currentItems.length > 0 ? (
                      currentItems.map((p, index) => (
                        <tr key={p.uuid || index}>
                          <td>{indexOfFirstItem + index + 1}</td>
                          <td>
                            {p.image_gallery && p.image_gallery.length > 0 ? (
                              <img
                                src={`${imgurl}${p.image_gallery.find((img) => img.is_featured)?.image_path || p.image_gallery[0].image_path}`}
                                alt={p.name}
                                className="rounded"
                                style={{ width: "60px", height: "60px", objectFit: "cover", border: "1px solid #eee" }}
                              />
                            ) : (
                              <div className="bg-light text-muted d-flex justify-content-center align-items-center" style={{ width: "60px", height: "60px", border: "1px solid #eee", fontSize: "12px" }}>No Image</div>
                            )}
                          </td>
                          <td>{p.name}</td>
                          <td>{p.sku}</td>
                          <td>{p.category_name}</td>
                          <td>{p.subcategory_name}</td>
                          <td> 
                            <div style={{ fontWeight: "600", color: "#000" }}>₹{p.price}</div>
                            {p.mrp && <div style={{ textDecoration: "line-through", color: "#888" }}>₹{p.mrp}</div>}
                          </td>
                          <td>
                            <span className={`badge ${p.current_stock > 0 ? 'bg-success' : 'bg-danger'}`}>
                              {p.current_stock > 0 ? `${p.current_stock} in stock` : 'Out of stock'}
                            </span>
                          </td>
                          <td>{new Date(p.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Link to={`/product/gallery/${p.uuid}`} className="btn btn-sm btn-outline-primary"><FaEdit /></Link>
                              <button className="btn btn-sm btn-outline-danger"><FaTrash /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="10" className="text-center text-muted py-4">No products found</td></tr>
                    )}
                  </tbody>
                </table>

                {/* PAGINATION UI */}
                {!loading && filteredProducts.length > itemsPerPage && (
                  <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                    <div className="text-muted small">
                      Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredProducts.length)}</strong> of <strong>{filteredProducts.length}</strong> entries
                    </div>
                    <nav>
                      <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                            <FaChevronLeft size={10} />
                          </button>
                        </li>
                        
                        {getPageNumbers().map((number, idx) => (
                          <li key={idx} className={`page-item ${currentPage === number ? 'active' : ''} ${number === '...' ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => number !== '...' && handlePageChange(number)}>
                              {number}
                            </button>
                          </li>
                        ))}

                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                            <FaChevronRight size={10} />
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* FILTER PANEL */}
          <div className={`filter-panel ${showFilter ? "open" : ""}`}>
            <div className="filter-header">
              <h5>Filters</h5>
              <button className="btn-close" onClick={() => setShowFilter(false)}></button>
            </div>
            <div className="filter-body">
              <div className="mb-3">
                <label>Search</label>
                <input type="text" className="form-control" name="search" placeholder="Name or SKU" value={filters.search} onChange={handleFilterChange} />
              </div>
              <div className="mb-3">
                <label>Category</label>
                <select className="form-control" name="category" value={filters.category} onChange={handleFilterChange}>
                  {categories.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
              <div className="mb-3">
                <label>Stock</label>
                <select className="form-control" name="stock" value={filters.stock} onChange={handleFilterChange}>
                  <option value="All">All</option>
                  <option value="InStock">In Stock</option>
                  <option value="OutOfStock">Out of Stock</option>
                </select>
              </div>
              <div className="mb-3">
                <label>Price Range</label>
                <div className="d-flex gap-2">
                  <input type="number" className="form-control" name="priceMin" placeholder="Min" value={filters.priceMin} onChange={handleFilterChange} />
                  <input type="number" className="form-control" name="priceMax" placeholder="Max" value={filters.priceMax} onChange={handleFilterChange} />
                </div>
              </div>
              <div className="mb-3">
                <label>Date From</label>
                <Flatpickr value={filters.dateFrom} onChange={(date) => handleDateChange("dateFrom", date)} options={{ dateFormat: "Y-m-d" }} className="form-control" />
              </div>
              <div className="mb-3">
                <label>Date To</label>
                <Flatpickr value={filters.dateTo} onChange={(date) => handleDateChange("dateTo", date)} options={{ dateFormat: "Y-m-d" }} className="form-control" />
              </div>
              <button className="btn btn-primary w-100" onClick={() => setShowFilter(false)}>Apply Filters</button>
            </div>
          </div>
        </div>
      </main>

      <style jsx>{`
        .filter-panel {
          position: fixed;
          right: -400px;
          top: 0;
          width: 350px;
          height: 100%;
          background: #fff;
          box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
          transition: right 0.3s ease;
          z-index: 1050;
          padding: 20px;
        }
        .filter-panel.open { right: 0; }
        .filter-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #ddd; margin-bottom: 15px; }
        .skeleton-line {
          height: 15px;
          width: 100%;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite;
          border-radius: 4px;
        }
        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* PAGINATION UI FIX */
        .pagination .page-link {
          color: #666;
          border: 1px solid #eee;
          padding: 5px 10px;
          font-size: 13px;
          min-width: 35px;
          text-align: center;
        }
        .pagination .page-item.active .page-link {
          background-color: #007bff;
          border-color: #007bff;
          color: #fff;
        }
        .pagination .page-item.disabled .page-link {
          background-color: #f8f9fa;
          color: #ccc;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
}

export default AllProducts;